import React from 'react';
import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, Search, Target, Award, Eye, ArrowUp, ArrowDown, Minus, CheckCircle, XCircle, AlertCircle, ChevronRight } from 'lucide-react';

const KeywordsPerformanceComparison: React.FC = () => {
  // Données de test pour les 3 options
  const mockKeywordsData = [
    { keyword: 'services suisse', volume: 470, position: 33, marque: false, opportunite: 'Forte — potentiel élevé' },
    { keyword: 'silver-international', volume: 249, position: 10, marque: true, opportunite: 'Faible — déjà acquis' },
    { keyword: 'silver-international avis', volume: 92, position: 30, marque: false, opportunite: 'Moyenne / Faible' },
    { keyword: 'silver-international contact', volume: 103, position: 33, marque: true, opportunite: 'Faible — navigation' },
    { keyword: 'silver-international services', volume: 37, position: 10, marque: true, opportunite: 'Faible — déjà acquis' }
  ];

  const getPositionColor = (position: number) => {
    if (position <= 10) return 'text-green-600 bg-green-100';
    if (position <= 30) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getPositionIcon = (position: number) => {
    if (position <= 10) return <CheckCircle className="w-4 h-4" />;
    if (position <= 30) return <AlertCircle className="w-4 h-4" />;
    return <XCircle className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Comparaison des designs Keywords Performance
          </h1>
          <p className="text-xl text-gray-600">
            Trois propositions modernes pour la section "Performance des Mots-clés"
          </p>
        </div>

        {/* Option 1: Analytics Dashboard Style */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 1: Analytics Dashboard Style</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                    <BarChart3 size={24} />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Performance des Mots-clés</h3>
                    <p className="text-gray-600">Positionnement SEO dans Google Suisse</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded-full">TOP 10</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">2</div>
                    <div className="text-sm text-gray-600">Mots-clés bien positionnés</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
                    <div className="flex items-center justify-between mb-2">
                      <Target className="w-5 h-5 text-orange-600" />
                      <span className="text-xs font-medium text-orange-600 bg-orange-200 px-2 py-1 rounded-full">VOLUME</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">951</div>
                    <div className="text-sm text-gray-600">Recherches mensuelles</div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2">
                      <Eye className="w-5 h-5 text-purple-600" />
                      <span className="text-xs font-medium text-purple-600 bg-purple-200 px-2 py-1 rounded-full">POTENTIEL</span>
                    </div>
                    <div className="text-2xl font-bold text-gray-900 mb-1">3</div>
                    <div className="text-sm text-gray-600">Opportunités détectées</div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left text-sm font-medium text-gray-500 border-b border-gray-200">
                          <th className="pb-3">Mot-clé</th>
                          <th className="pb-3">Volume</th>
                          <th className="pb-3">Position</th>
                          <th className="pb-3">Opportunité</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {mockKeywordsData.slice(0, 3).map((keyword, index) => (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-3 font-medium text-gray-900">{keyword.keyword}</td>
                            <td className="py-3 text-gray-600">{keyword.volume}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPositionColor(keyword.position)}`}>
                                #{keyword.position}
                              </span>
                            </td>
                            <td className="py-3 text-gray-600 text-xs">{keyword.opportunite}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 text-white">
                  <h4 className="text-lg font-bold mb-4">Analyse SEO</h4>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Mots-clés détectés</span>
                      <span className="font-bold text-blue-400">5</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Position moyenne</span>
                      <span className="font-bold text-orange-400">22</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Potentiel trafic</span>
                      <span className="font-bold text-green-400">+476/mois</span>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <h5 className="font-medium mb-3">Recommandations</h5>
                    <div className="space-y-2 text-sm text-gray-300">
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                        <span>Optimiser 3 mots-clés prioritaires</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1 h-1 bg-blue-400 rounded-full"></div>
                        <span>Créer du contenu ciblé</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Option 2: Modern Card Grid Style */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 2: Modern Card Grid Style</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 border border-blue-200 text-blue-700 text-sm font-medium mb-4">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse mr-2"></div>
                <Search className="w-4 h-4 mr-2" />
                Analyse SEO
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">
                Performance des <span className="text-blue-600">Mots-clés</span>
              </h3>
              <p className="text-lg text-gray-600">Positionnement SEO dans Google Suisse</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {mockKeywordsData.map((keyword, index) => (
                <div key={index} className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-2 rounded-lg ${getPositionColor(keyword.position)}`}>
                      {getPositionIcon(keyword.position)}
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      Vol. {keyword.volume}
                    </span>
                  </div>
                  
                  <h4 className="font-semibold text-gray-900 mb-2 truncate">{keyword.keyword}</h4>
                  
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">Position</span>
                    <span className={`font-bold ${keyword.position <= 10 ? 'text-green-600' : keyword.position <= 30 ? 'text-orange-600' : 'text-red-600'}`}>
                      #{keyword.position}
                    </span>
                  </div>
                  
                  <div className="text-xs text-gray-500 bg-gray-100 rounded-lg p-2">
                    {keyword.opportunite}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Recommandations SEO</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Le nombre limité de mots-clés détectés (5) indique un potentiel inexploité. Une stratégie SEO ciblée pourrait multiplier votre visibilité par 3.
                  </p>
                  <p className="text-sm text-gray-600">
                    En travaillant 3 à 5 mots-clés non-brand, vous pourriez capter jusqu'à 476 visiteurs supplémentaires par mois.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Option 3: Tech Dashboard Style (Couleurs TestPage) */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 3: Tech Dashboard Style (Couleurs TestPage)</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analyse SEO
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Performance des <span className="text-orange-600">Mots-clés</span>
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Positionnement SEO dans Google Suisse et opportunités d'amélioration détectées
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <div className="text-orange-600 text-sm font-medium mb-1">Mots-clés</div>
                    <div className="text-2xl font-bold text-gray-900">5 détectés</div>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                    <div className="text-orange-600 text-sm font-medium mb-1">Potentiel</div>
                    <div className="text-2xl font-bold text-gray-900">+476/mois</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Target className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">3 opportunités à fort potentiel identifiées</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Position moyenne améliorable (22e position)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Award className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Stratégie SEO recommandée pour multiplier le trafic</span>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-lg font-bold text-white">Analyse détaillée</h4>
                    <span className="text-xs font-medium text-orange-400 bg-orange-500/20 px-3 py-1 rounded-full">
                      5 mots-clés
                    </span>
                  </div>
                  
                  <div className="space-y-4 mb-6">
                    {mockKeywordsData.slice(0, 3).map((keyword, index) => (
                      <div key={index} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white font-medium text-sm truncate">{keyword.keyword}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            keyword.position <= 10 ? 'bg-green-900/50 text-green-400' :
                            keyword.position <= 30 ? 'bg-orange-900/50 text-orange-400' :
                            'bg-red-900/50 text-red-400'
                          }`}>
                            #{keyword.position}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-400">Vol. {keyword.volume}</span>
                          <span className={`${keyword.marque ? 'text-blue-400' : 'text-orange-400'}`}>
                            {keyword.marque ? 'Marque' : 'Générique'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-amber-200">Potentiel SEO inexploité détecté</p>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-green-900/30 border border-green-700/50 rounded-lg">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-green-200">
                        <p className="font-medium mb-1">Recommandations :</p>
                        <ul className="space-y-1 text-xs">
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Optimiser 3 mots-clés prioritaires</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Créer du contenu SEO ciblé</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="w-1 h-1 bg-orange-400 rounded-full"></div>
                            <span>Améliorer les balises techniques</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Option 4: Table Enhanced Style (Basé sur l'actuel mais modernisé) */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Option 4: Table Enhanced Style (Design actuel modernisé)</h2>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-sm font-medium mb-6">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse mr-2"></div>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analyse SEO
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">
                  Performance des <span className="text-orange-600">Mots-clés</span> dans Google Suisse
                </h3>
                <p className="text-lg text-gray-600 mb-6">
                  Positionnement SEO et visibilité avec analyse détaillée par mot-clé
                </p>
                
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      <span className="text-orange-600 font-bold">5</span> mots-clés trouvés pour{' '}
                      <span className="font-mono text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        silver-international.com
                      </span>
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Target className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Analyse complète du positionnement SEO</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Opportunités d'amélioration identifiées</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                      <Award className="w-4 h-4" />
                    </div>
                    <span className="text-gray-700 font-medium">Recommandations personnalisées incluses</span>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-4 border-b border-orange-200">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-gray-900">Tableau des performances</h4>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Page 1 / 1</span>
                        <button className="text-xs font-medium text-orange-600 hover:text-orange-800 transition-colors flex items-center gap-1">
                          Tout développer
                          <ChevronRight className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <th className="px-4 py-3">Mot-clé</th>
                          <th className="px-4 py-3">Volume</th>
                          <th className="px-4 py-3">Position</th>
                          <th className="px-4 py-3">Marque ?</th>
                          <th className="px-4 py-3">Opportunité</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {mockKeywordsData.map((keyword, index) => (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              <div className="font-medium text-gray-900 text-sm">{keyword.keyword}</div>
                            </td>
                            <td className="px-4 py-4">
                              <div className="text-sm text-gray-600">{keyword.volume}</div>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                keyword.position <= 10 ? 'bg-green-100 text-green-800' :
                                keyword.position <= 30 ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {keyword.position <= 10 ? (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                ) : keyword.position <= 30 ? (
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                ) : (
                                  <XCircle className="w-3 h-3 mr-1" />
                                )}
                                {keyword.position}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                keyword.marque ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {keyword.marque ? (
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                ) : (
                                  <XCircle className="w-3 h-3 mr-1" />
                                )}
                                {keyword.marque ? 'Oui' : 'Non'}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <div className={`text-xs font-medium ${
                                keyword.opportunite.includes('Forte') ? 'text-green-600' :
                                keyword.opportunite.includes('Moyenne') ? 'text-orange-600' :
                                'text-red-600'
                              }`}>
                                {keyword.opportunite}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section Recommandations style sombre */}
            <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-orange-500/20 rounded-lg flex-shrink-0">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                    <span>Recommandations :</span>
                    <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full font-medium">
                      Potentiel élevé
                    </span>
                  </h4>
                  <div className="space-y-3 text-sm text-gray-300">
                    <p className="leading-relaxed">
                      Le nombre limité de mots-clés détectés <span className="font-semibold text-orange-400">(5)</span> indique un potentiel inexploité. 
                      Une stratégie SEO ciblée pourrait <span className="font-semibold text-orange-400">multiplier votre visibilité par 3</span> et attirer un public 
                      hautement qualifié vers votre site.
                    </p>
                    <p className="leading-relaxed">
                      En travaillant 3 à 5 mots-clés non-brand, vous pourriez capter jusqu'à{' '}
                      <span className="font-semibold text-green-400 bg-green-900/30 px-2 py-1 rounded">476 visiteurs supplémentaires par mois</span>{' '}
                      avec une intention réelle d'achat.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            Choisissez le design qui correspond le mieux à vos attentes
          </p>
          <div className="space-x-4">
            <button className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
              Implémenter Option 4
            </button>
            <button className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
              Retour à TestPage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeywordsPerformanceComparison;
